/*! vue-page-title v2.2.2
 * Vue.js html/page title manager
 * https://github.com/vinicius73/vue-page-title
 * (c) 2018-2023 Vinicius Reis <luiz.vinicius73@gmail.com>
 */

import { inject, ref, watch, computed } from "vue";
const isFunction = (val) => typeof val === "function";
const safeString = (st) => (st || "").toString();
const isBrowser = () => typeof document !== "undefined";
const buildPageTitle = (value, options = {}) => {
  const prefix = safeString(options.prefix);
  const suffix = safeString(options.suffix);
  let separator = safeString(options.separator);
  if (separator.length === 0) {
    separator = " ";
  }
  if (value.length === 0) {
    if (prefix.length > 0 && suffix.length > 0) {
      return `${prefix}${separator}${suffix}`.trim();
    } else {
      return `${prefix}${suffix}`.trim();
    }
  }
  if (prefix.length > 0) {
    value = `${prefix}${separator}${value}`;
  }
  if (suffix.length > 0) {
    value = `${value}${separator}${suffix}`;
  }
  return value.trim();
};
const setPageTitle = (value, options = {}) => {
  if (!isBrowser()) {
    console.warn("vue-page-title: no browser enviroment");
    return;
  }
  value = safeString(value);
  const { setTitleMethod } = options;
  const title = buildPageTitle(value, options);
  if (setTitleMethod != null) {
    setTitleMethod(title);
    return;
  }
  document.title = title;
};
const setupRouter = (router, setTitle) => {
  router.afterEach((to, from) => {
    const { meta } = to;
    if ((meta == null ? void 0 : meta.title) != null) {
      setTitle(meta.title);
    }
  });
};
const PAGE_TITLE = Symbol("title");
const SET_PAGE_TITLE = Symbol("set-title");
const pageTitleMixin = {
  created() {
    const { title } = this.$options;
    if (title === void 0) {
      return;
    }
    if (isFunction(title)) {
      this.$watch(
        () => title.call(this, this),
        (val) => {
          this.$setPageTitle(val);
        },
        { immediate: true }
      );
      return;
    }
    this.$setPageTitle(title);
  }
};
const useTitle = (initial) => {
  const title = inject(PAGE_TITLE, ref(""));
  const setTitle = inject(SET_PAGE_TITLE, () => {
  });
  if (typeof initial === "string") {
    setTitle(initial);
  } else if (initial != null) {
    watch(initial, setTitle, { immediate: true });
  }
  return {
    title: computed(() => title == null ? void 0 : title.value),
    setTitle
  };
};
const pageTitle = (options = {}) => {
  const $title = ref("");
  const setTitle = (val) => {
    setPageTitle(val, options);
    $title.value = val;
  };
  const installedApps = /* @__PURE__ */ new WeakSet();
  if (options.router != null) {
    setupRouter(options.router, setTitle);
  }
  const install = (app) => {
    if (installedApps.has(app)) {
      return;
    }
    installedApps.add(app);
    if (options.mixin ?? false) {
      app.mixin(pageTitleMixin);
    }
    app.provide(PAGE_TITLE, $title);
    app.provide(SET_PAGE_TITLE, setTitle);
    Object.defineProperty(app.config.globalProperties, "$title", {
      get: () => $title.value
    });
    Object.defineProperty(app.config.globalProperties, "$setPageTitle", {
      get: () => setTitle
    });
  };
  return { install };
};
export {
  pageTitle,
  pageTitleMixin,
  useTitle
};
